import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Database, Server, Crown } from "lucide-react";

export default function Payment() {
  const [selectedPlan, setSelectedPlan] = useState("Pro");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const plans = [
    {
      name: "Starter",
      icon: <Database className="w-5 h-5 text-gray-400" />,
      price: 10,
      features: [
        "Up to 10K vectors/month",
        "Basic migration tools",
        "Community support",
      ],
    },
    {
      name: "Pro",
      icon: <Server className="w-5 h-5 text-gray-400" />,
      price: 29,
      features: [
        "Up to 100K vectors/month",
        "Batch ingestion pipeline",
        "Priority migration queue",
        "Email support",
      ],
      popular: true,
    },
    {
      name: "Enterprise",
      icon: <Crown className="w-5 h-5 text-gray-400" />,
      price: 99,
      features: [
        "Unlimited vectors/month",
        "Custom ingestion workflow",
        "Dedicated migration specialist",
        "API + SSO Integration",
      ],
    },
  ];

  const total = plans.find((p) => p.name === selectedPlan)?.price || 0;

  // --- Dummy Payment Logic ---
  const handleDummyPayment = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      alert(`✅ Payment successful! You subscribed to the ${selectedPlan} plan.`);
      navigate("/dashboard");
    }, 1500);
  };

  return (
    <div className="min-h-screen w-full bg-black text-white flex flex-col items-center py-12 px-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <div className="flex justify-center items-center gap-3 mb-2">
          <video
            src="/vector-icon.mp4"
            className="w-10 h-10 rounded-full object-cover"
            autoPlay
            loop
            muted
          />
          <h1 className="text-3xl font-bold text-white">
            Vector Migration Platform
          </h1>
        </div>
        <p className="text-gray-400 text-sm">
          Choose your plan and streamline your data vector ingestion workflow.
        </p>
      </motion.div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-6xl mb-12">
        {plans.map((plan) => (
          <motion.div
            key={plan.name}
            whileHover={{ scale: 1.04 }}
            transition={{ duration: 0.2 }}
            className={`relative rounded-xl p-6 bg-[#111111] border transition-all duration-300 ${
              selectedPlan === plan.name
                ? "border-gray-300 shadow-[0_0_20px_rgba(255,255,255,0.08)]"
                : "border-[#1f1f1f] hover:border-gray-600"
            }`}
          >
            {plan.popular && (
              <div className="absolute top-3 right-3 bg-white text-black text-xs px-3 py-1 rounded-full font-medium tracking-wide">
                MOST POPULAR
              </div>
            )}

            <div className="flex items-center justify-center gap-2 mb-3">
              {plan.icon}
              <h3 className="text-lg font-medium">{plan.name}</h3>
            </div>

            <p className="text-4xl font-bold text-center text-white mb-1">
              ${plan.price}
              <span className="text-gray-400 text-sm font-normal">/month</span>
            </p>

            <ul className="mt-5 space-y-2 text-gray-400 text-sm">
              {plan.features.map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-gray-500" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={() => setSelectedPlan(plan.name)}
              className={`w-full mt-6 py-2.5 rounded-md font-medium transition-all ${
                selectedPlan === plan.name
                  ? "bg-white text-black shadow-md"
                  : "bg-transparent border border-gray-600 text-gray-200 hover:bg-gray-900"
              }`}
            >
              {selectedPlan === plan.name ? "Selected" : "Select Plan"}
            </button>
          </motion.div>
        ))}
      </div>

      {/* Summary */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-[#111111] border border-[#1f1f1f] rounded-xl p-8 shadow-md w-full max-w-md"
      >
        <h2 className="text-lg font-medium mb-5 text-white">Payment Summary</h2>

        <div className="space-y-3 text-sm text-gray-400">
          <div className="flex justify-between">
            <span>Selected Plan:</span>
            <span className="text-white">{selectedPlan}</span>
          </div>
          <div className="flex justify-between">
            <span>Price:</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>

        <div className="border-t border-gray-700 my-4"></div>

        <div className="flex justify-between items-center text-lg font-semibold">
          <span>Total Due Today:</span>
          <span className="text-white">${total.toFixed(2)}</span>
        </div>

        <button
          onClick={handleDummyPayment}
          disabled={loading}
          className={`w-full mt-6 py-3 rounded-md font-semibold transition-all ${
            loading
              ? "bg-gray-800 text-gray-500 cursor-not-allowed"
              : "bg-white text-black hover:bg-gray-200"
          }`}
        >
          {loading ? "Processing..." : "Proceed to Pay"}
        </button>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate("/")}
            className="text-gray-400 text-sm hover:text-white transition-colors"
          >
            Back to Home
          </button>
        </div>
      </motion.div>

      <p className="text-gray-500 text-xs mt-10">
        © 2025 Vector Migration • Terms • Privacy
      </p>
    </div>
  );
}
